import styled from '@emotion/styled';
import type { ComponentProps, ComponentType } from 'react';
import { outlinedInputClasses } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { DatePicker } from 'components/inputs/DatePicker';
import { colors } from 'styles/theme/colors';

// Use DatePicker directly - provide fallback for test environments where component might be undefined
// This prevents "styled element with undefined component" errors during module loading in tests
const DatePickerBase = (DatePicker || 'div') as typeof DatePicker;

/**
 * Table Emotion Styled Components
 * Provides proper containment, overflow handling, and height management
 * for tables nested in various container layers
 */

/* Main table container - provides CSS containment and flex layout */
export const TableContainerStyled = styled.div`
  /* CSS containment for better isolation and performance */
  contain: layout style paint;

  /* Flex layout for proper height distribution */
  flex: 1;
  display: flex;
  flex-direction: column;

  /* Critical for nested flex containers */
  min-height: 0;
  height: 100%;

  /* Width constraints to match height management */
  min-width: 0; /* Allow flex children to shrink */
  width: 100%; /* Constrain to parent width */
  max-width: 100%; /* Prevent overflow beyond parent */

  /* Positioning context for absolute children */
  position: relative;

  /* Prevent content from overflowing container */
  overflow: hidden;

  /* Override legacy Table component's OuterContainer overflow */
  /* This allows horizontal scrolling to work */
  & > div > div[id='megayaee'] {
    overflow: visible !important;
  }

  /* Ensure the scroll container inside can show scrollbars */
  & > div > div[id='megayaee'] > div {
    overflow-x: auto !important;
    overflow-y: auto !important;
  }
`;

/* Scroll area wrapper - handles overflow scrolling */
export const ScrollArea = styled.div`
  /* Take remaining space in flex container */
  flex: 1 1 0;

  /* Enable scrolling */
  overflow-x: auto;
  overflow-y: auto;

  /* Critical for flex children to scroll properly */
  min-height: 0;
  min-width: 0; /* ADD: Allow horizontal shrinking */

  /* Full width */
  width: 100%;
  max-width: 100%; /* ADD: Prevent overflow */

  /* Positioning context */
  position: relative;
`;

/* Inner content wrapper */
export const TableContent = styled.div`
  width: 100%;
  min-width: fit-content;
`;

/* Variant for fixed height containers */
export const FixedHeightContainer = styled(TableContainerStyled)<{
  height?: string;
  width?: string /* ADD: Optional fixed width */;
}>`
  height: ${p => p.height || '400px'};
  width: ${p => p.width || '100%'}; /* ADD: Support fixed width */
  flex: none;
`;

/* Variant for auto height (content-based) */
export const AutoHeightContainer = styled(TableContainerStyled)<{
  maxHeight?: string;
  maxWidth?: string /* ADD: Optional max width */;
}>`
  height: auto;
  max-height: ${p => p.maxHeight || '100%'};
  max-width: ${p => p.maxWidth || '100%'}; /* ADD: Support max width */
`;

/* Full page variant - takes entire viewport height minus offset */
export const FullPageContainer = styled(TableContainerStyled)<{ offsetTop?: string }>`
  height: calc(100vh - ${p => p.offsetTop || '0px'});
  max-width: 100vw; /* ADD: Constrain to viewport width */
`;

/* Nested container variant - extra containment for deeply nested tables */
export const NestedContainer = styled(TableContainerStyled)`
  /* Stronger containment */
  contain: strict;

  /* Ensure isolation from parent styles */
  isolation: isolate;

  /* ADD: Extra width constraint for nested context */
  max-width: 100%;
`;
/* Loading state overlay */
export const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
`;

/* Empty state container */
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${p => p.theme.s.xxl} ${p => p.theme.s.md};
  min-height: 200px;
`;

/* Header area (filters, search, etc.) */
export const HeaderArea = styled.div`
  flex-shrink: 0;
  position: relative;
  z-index: 5;
`;

/* Footer area (pagination, etc.) */
export const FooterArea = styled.div`
  flex-shrink: 0;
  position: relative;
  z-index: 5;
`;

/* Wrapper for table content - flex column so table area and bulk bar stack vertically */
export const TableContentWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* Hide internal pagination from Table component - we use PaginationFooter instead */
  /* Target the pagination container (TableScrollArea > Table > ... > div containing record-count) */
  & > div > div > div > div > div:has(> [data-testid='record-count']) {
    display: none;
  }
`;

/* Scrollable area that contains the Table and floating bulk bar - takes remaining space */
export const TableScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
`;

/* ============================================
   Column Cell Styles
   ============================================ */

/* Generic cell container - provides consistent vertical alignment across all cell types */
export const CellContainer = styled.div`
  display: flex;
  align-items: center;
  min-height: 40px;
  width: 100%;
`;

/**
 * Read-only container for tag/chip (multi-select) cells. Matches the horizontal
 * padding of the editable read view (ReadWrapper) and the single-select
 * ReadOnlyContainer (both `theme.s.sm`) so tags line up vertically whether or not
 * the row is editable.
 */
export const TagsReadOnlyContainer = styled(CellContainer)`
  padding: 0 ${p => p.theme.s.sm};
`;

/* Link cell - clickable text that navigates */
export const LinkCell = styled.a`
  color: inherit;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

/* Masked cell - for sensitive data like API keys */
export const MaskedCell = styled.span`
  font-family: monospace;
  letter-spacing: 0.5px;
`;

/* Actions cell - container for action buttons */
export const ActionsCell = styled.div`
  display: flex;
  gap: ${p => p.theme.s.xxs};
  justify-content: flex-end;
`;

/* Editable cell row - single flex row so tooltip/cell wrapper sees one root; keeps dots on the right */
export const EditableCellRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
`;

/* Editable cell content area - shrinks so dots menu stays on the same line */
export const EditableCellContentArea = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

/* Wrapper for dots menu button - keeps it on the same row as cell content */
export const DotsMenuButtonWrapper = styled.div`
  max-width: 18px;
  flex-shrink: 0;
`;

/* Styled date picker - for inline date editing cells */
export const StyledDatePicker = styled(DatePickerBase as ComponentType<unknown>)(({ theme }) => ({
  width: '90%',
  '&.MuiFormControl-root': {
    flexDirection: 'column-reverse',
    gap: '3px',
  },
  '& .MuiInput-root': {
    color: theme.palette.text.secondary,
  },
  '& .MuiInput-underline::before': {
    borderBottom: `0.5px solid ${theme.palette.border.primarySubtle}`,
  },
  '& .MuiInput-underline::after': {
    borderBottom: 'none',
  },
  '& .MuiInput-underline:hover:not(.Mui-disabled)::before': {
    borderBottom: `0.5px solid ${theme.palette.border.primarySubtle}`,
  },
})) as ComponentType<ComponentProps<typeof DatePicker>>;

/* ============================================
   Description Field Styles
   ============================================ */

/**
 * Cell container with slightly more height for description preview
 * Displays up to 2 lines with ellipsis for overflow
 */
export const DescriptionCellContainer = styled.div`
  min-height: 40px;
  max-height: 56px;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  line-height: 1.4;
  padding: 4px 0;

  /* Inner wrapper for line clamping */
  & > * {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: normal;
    word-break: break-word;
  }
`;

/**
 * Text element for description that allows wrapping
 */
export const DescriptionText = styled.span`
  white-space: normal;
  word-break: break-word;
`;

/**
 * Textarea for inline editing of descriptions
 * Larger input area for longer text content
 */
export const CellTextarea = styled.textarea`
  width: 100%;
  min-height: 60px;
  padding: 8px;
  border: 1px solid ${p => p.theme.palette.border.primary};
  border-radius: 4px;
  font-family: inherit;
  font-size: inherit;
  resize: vertical;

  :focus-visible {
    outline: none;
    border-color: ${p => p.theme.palette.border.accentPrimary};
  }
`;

/**
 * Styles for description textarea in form modals
 * Used with MUI TextField multiline component
 * Matches the padding of standard outlined TextInput for consistent alignment.
 * Theme-aware so outline is continuous (no notch gap) and focus/hover match design system.
 */
export const descriptionFormSx: SxProps<Theme> = (theme: Theme) => ({
  '& .MuiOutlinedInput-notchedOutline legend': {
    maxWidth: 0,
  },
  [`& .${outlinedInputClasses.root}`]: {
    padding: 0,
    alignItems: 'flex-start',
    borderRadius: '8px',
    backgroundColor: theme.palette.bg.primary,
    marginTop: '6px',
    marginBottom: '6px',
    [`& .${outlinedInputClasses.notchedOutline}`]: {
      borderRadius: '8px',
      borderColor: theme.palette.border.primary,
      borderWidth: '0.5px',
    },
    '&:hover:not(.Mui-disabled)': {
      [`& .${outlinedInputClasses.notchedOutline}`]: {
        borderColor: theme.palette.border.brand,
      },
    },
    [`&.${outlinedInputClasses.focused}`]: {
      [`& .${outlinedInputClasses.notchedOutline}`]: {
        borderColor: theme.palette.border.brand,
        borderWidth: '1px',
        boxShadow:
          '0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px rgba(22, 105, 99, 0.24)',
      },
    },
    // Disabled state styling - use same background as ComboBox for consistency
    [`&.${outlinedInputClasses.disabled}`]: {
      backgroundColor:
        (theme.palette.bg as { disabledSubtle?: string })?.disabledSubtle ??
        theme.palette.action.disabledBackground,
      [`& .${outlinedInputClasses.notchedOutline}`]: {
        borderColor: theme.palette.action.disabled,
      },
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '16.5px 14px',
    paddingBottom: '32px', // Extra space for character counter
    resize: 'vertical',
    // Disabled input text color - match ComboBox disabled text color
    '&.Mui-disabled': {
      WebkitTextFillColor:
        (theme.palette.text as { disabled?: string })?.disabled ?? theme.palette.action.disabled,
      color:
        (theme.palette.text as { disabled?: string })?.disabled ?? theme.palette.action.disabled,
      '&::placeholder': {
        WebkitTextFillColor:
          (theme.palette.text as { disabled?: string })?.disabled ?? theme.palette.action.disabled,
        color:
          (theme.palette.text as { disabled?: string })?.disabled ?? theme.palette.action.disabled,
        opacity: 1,
      },
    },
  },
});

/* ============================================
   Multi-Select Field Styles
   ============================================ */

/**
 * Wrapper for inline multiselect editor (API and static paths).
 * Ensures consistent width and min-width for both MultiselectAutocomplete and MultiSelectDropdown.
 */
export const MultiselectEditorContainer = styled.div`
  width: 100%;
  min-width: 0;
`;

/**
 * Full-width wrapper used as the anchor for the inline multi-select error tooltip.
 * Nested inside MultiselectEditorContainer (not wrapping it) so it never sits between
 * the ClickAwayListener and the editor's DOM node.
 */
export const MultiselectErrorAnchor = styled.div`
  width: 100%;
`;

/**
 * Container for multi-select chips/tags display
 * Used in read-only and editable multi-select cells
 */
export const MultiSelectChipsContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 4px;
  max-width: 100%;
  overflow: hidden;
`;

/* ============================================
   Tags Field Styles
   ============================================ */

/**
 * Container for tags display in table cells
 * Wraps tag elements with proper spacing
 */
export const TagsContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 4px;
  align-items: center;
`;

/**
 * Individual tag element with boundary styling
 * Displays as a pill-shaped chip with border and background
 * Uses white background with grey border for clean appearance
 */
export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 6px;
  background-color: ${colors.white};
  border: 1px solid ${colors.grey[200]};
  font-size: 0.75rem;
  line-height: 1.4;
  white-space: nowrap;
  color: ${colors.grey[700]};
`;

/**
 * Invalid tag element for displaying values that don't match allowed options
 * Used when legacy data contains values no longer in the allowed values list
 * Displays with red border and text to indicate validation error
 */
export const InvalidTag = styled(Tag)`
  border-color: ${colors.error[300]};
  color: ${colors.error[700]};
  background-color: ${colors.error[50]};
`;

/**
 * Truncated text element for single-line text with ellipsis
 * Used for text custom fields to prevent layout overflow
 */
export const TruncatedText = styled.span`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

/* ============================================
   Form Input Styles
   ============================================ */

/**
 * Common styles for form inputs in table modals
 * Removes left padding from MUI input components
 */
export const formInputSx = {
  '& .MuiInputBase-root': {
    paddingLeft: 0,
  },
  '& .MuiInputBase-input': {
    paddingLeft: 0,
  },
};
